import { useSupportSheet } from "../../components/SupportSheet";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useStore } from "../../store/globalState";
import { useSessionStore } from "../../store/useSessionStore";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { BodyCopy } from "../../components/BodyCopy";
import {
  AuraLayout,
  BankIcon,
  Button,
  CashSpecialIcon,
  ExchangeIcon,
  Layout,
  SupportIcon,
  Title,
  WalletIcon,
  WarningIcon,
} from "lola-framework-ui-test";
import { APP_ROUTES, FUND_REQUEST_ROUTES, TITLE_COLOR } from "../../constants";
import { FlagCircle } from "../../components/FlagCircle";
import { QuoteInfo } from "../../components/fund-request/QuoteInfo";
import { amountFormatter, formatLabeledAmount, formatMoney } from "../../utils";
import { compareSendToBound } from "../../utils/fundingLimits";
import { PayoutInfo } from "../../components/PayoutInfo";
import { FooterDetailSummary } from "../../components/FooterDetailSummary";
import { Popup } from "../../components/Popup";
import {
  SelectFundingMethodSlot,
  type FlatFundingPick,
} from "../../components/fund-request/SelectFundingMethodSlot";
import { AmountOutOfRangeInfo } from "../../components/AmountOutOfRangeInfo";
import { paymentType } from "../../interfaces/payment";
import { formatBankAccountLabel } from "../../utils/bankAccountLabel";
import type { ICardPaymentMethod } from "../../interfaces/payment-method";
import masterCardLogo from "../../assets/master_card_icon.svg";
import visaLogo from "../../assets/visa_color_icon.svg";
import { useQuote } from "../../hooks/useQuote";
import { useGalleryPickerLoading } from "../../hooks/useGalleryPickerLoading";
import { COUNTRY_CURRENCY } from "../../hooks/useExchangeRate";
import {
  buildMockQuote,
  MOCK_EXCHANGE_RATE,
  mockCards,
  mockSelectedPayment,
} from "../../gallery/mockData";
import {
  applyFirstSendSeed,
  isFirstSendWarningVisible,
} from "../../gallery/firstSendMock";
import { FirstSendWarning } from "../../components/first-send/FirstSendWarning";
import { useGalleryFirstSend } from "../../hooks/useGalleryFirstSend";
import {
  fundRequestAvailableFunding,
  fundRequestFundingDetails,
} from "../../gallery/fundRequestMock";
import {
  categoryFromFundingMethod,
  type FundingMethod,
} from "../../utils/sendCatalog";
import {
  isDestinationCurrencyEditable,
  isPayoutEditable,
} from "../../utils/payoutAnchor";
import { useComposeStore } from "../../store/useComposeStore";
import { claimDefaultPayoutSelection } from "../../utils/claimPayoutCompose";
import {
  payoutRowFromClaim,
  payoutRowFromSelection,
} from "../../utils/fundRequestPayoutDisplay";
import { FundRequestPayoutPopups } from "../../components/fund-request/FundRequestPayoutPopups";
import { SelectDestinationCurrencySlot } from "../../components/fund-request/SelectDestinationCurrencySlot";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { Navbar } from "../../components/Navbar";


/** Map widget payment types to API payment method types. */
const mapPaymentTypeToMethod = (type: paymentType | undefined): string | null => {
  if (!type) return null;
  switch (type) {
    case paymentType.CARD:
      return "card";
    case paymentType.BANK:
      return "bank_account";
    case paymentType.CASH:
      return "cash";
    default:
      return null;
  }
};

const fundingFromPayment = (
  type: paymentType | undefined
): FundingMethod | null => {
  if (!type) return null;
  switch (type) {
    case paymentType.CARD:
      return "DEBIT_CARD";
    case paymentType.CASH:
      return "CASH_DEPOSIT";
    case paymentType.BANK:
      return "ACH";
    default:
      return null;
  }
};

const toSavedCards = (): ICardPaymentMethod[] =>
  mockCards
    .filter(
      (card): card is typeof card & { cardId: string; last4: string; brand: string } =>
        Boolean(card.cardId && card.last4 && card.brand)
    )
    .map((card, index) => ({
      paymentMethodId: card.cardId,
      last4: card.last4,
      brand: card.brand,
      expiryMonth: "12",
      expiryYear: "27",
      cardholderName: card.fullName ?? undefined,
      enabled: true,
      isDefault: false,
      fundingMethod: index === 0 ? "DEBIT_CARD" : "CREDIT_CARD",
    }));

/**
 * `primaryMesh` is usually a gradient string, which is invalid for `color` and
 * `border-color`. The outlined icon in the rate pill needs a solid value, so it
 * falls back to the gradient's first stop.
 */
const solidStop = (color: string) =>
  color.match(/#[0-9a-f]{3,8}|rgba?\([^)]*\)/i)?.[0] ?? color;

/**
 * Fund-request transfer composer (gallery-safe). Flat funding picker with
 * ineligible saved cards greyed out. On confirm navigates to sending-money.
 */
export const TransferPage = memo(() => {
  const { t } = useTranslation("fund-request");
  const {
    theme,
    amountState,
    paymentState,
    setPaymentState,
    setQuoteState,
    setAmountState,
  } = useStore();
  const { session } = useSessionStore();
  const { amount, anchor: amountAnchor = "receive" } = amountState;
  const config = session.data?.config;
  const amountSeededRef = useRef(false);
  const colorConfig = theme.whiteView;
  const {
    backgroundBtn,
    textColorBtn,
    title,
    bodyCopy,
    dropzoneColors,
    bannerHighlightBackground,
    bannerHighlightColor,
    bannerHighlightIconColors,
  } = colorConfig as typeof colorConfig & {
    bannerHighlightBackground?: string;
    bannerHighlightColor?: string;
    bannerHighlightIconColors?: string[];
  };
  const navigate = useNavigate();
  const { openSupport, supportSheet } = useSupportSheet("fund-request");
  const [popup, setPopup] = useState<
    "funding" | "amountLimit" | "payout" | "sourceCurrency" | "destinationCurrency" | null
  >(null);
  const amountEditable = config?.allowAmountEdit === true;
  const payoutEditable = isPayoutEditable(config);
  const destinationCurrencyEditable =
    isDestinationCurrencyEditable(config);
  const composeSelection = useComposeStore((s) => s.selection);
  const setComposeSelection = useComposeStore((s) => s.setSelection);
  const storedDestinationCurrency = useComposeStore(
    (s) => s.destinationCurrency,
  );
  const setDestinationCurrency = useComposeStore(
    (s) => s.setDestinationCurrency,
  );
  const storedSourceCurrency = useComposeStore((s) => s.sourceCurrency);
  const setSourceCurrency = useComposeStore((s) => s.setSourceCurrency);
  const [payoutRevision, setPayoutRevision] = useState(0);
  const fundingPickerLoading = useGalleryPickerLoading(popup === "funding");
  const [shakePayment, setShakePayment] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [showExcratePopup, setShowExcratePopup] = useState(false);

  const sourceCountry = session.data?.config?.sourceCountry || "US";
  const destinationCountry = session.data?.config?.destinationCountry || "MX";
  const claimSourceCurrency =
    config?.sourceCurrency || COUNTRY_CURRENCY[sourceCountry] || "USD";
  const sourceCurrency = destinationCurrencyEditable
    ? (storedSourceCurrency ?? claimSourceCurrency)
    : claimSourceCurrency;
  const claimDestinationCurrency =
    config?.destinationCurrency ||
    COUNTRY_CURRENCY[destinationCountry] ||
    "MXN";
  const destinationCurrency = destinationCurrencyEditable
    ? (storedDestinationCurrency ?? claimDestinationCurrency)
    : claimDestinationCurrency;
  const destinationCurrencyOptions = useMemo(
    () =>
      ["MXN", "USD"].map((currencyCode) => ({
        countryCode: destinationCountry,
        countryName: "Mexico",
        currencyCode,
      })),
    [destinationCountry],
  );
  const sourceCurrencyOptions = useMemo(
    () => {
      const codes = new Set<string>([claimSourceCurrency]);
      for (const corridor of config?.corridors ?? []) {
        if (corridor.originCountryCode === sourceCountry) {
          for (const code of corridor.sourceCurrencyCodes ?? []) {
            codes.add(code);
          }
        }
      }
      return [...codes].map((currencyCode) => ({
        countryCode: sourceCountry,
        countryName: "United States",
        currencyCode,
      }));
    },
    [claimSourceCurrency, config?.corridors, sourceCountry],
  );
  const sourceAmountPrefix = sourceCurrency === "USD" ? "$" : "";
  const destinationAmountPrefix = destinationCurrency === "USD" ? "$" : "";

  useEffect(() => {
    if (amountSeededRef.current) return;
    amountSeededRef.current = true;
    if (config?.amount != null && config.amount > 0) {
      const raw = String(config.amount);
      setAmountState({
        amount: formatMoney(raw) || raw,
        anchor: "receive",
      });
    }
  }, [config?.amount, setAmountState]);

  const firstSend = useGalleryFirstSend();
  useEffect(() => {
    if (!firstSend) return;
    return applyFirstSendSeed({ payment: mockSelectedPayment });
  }, [firstSend]);

  useEffect(() => {
    if (!destinationCurrencyEditable || storedDestinationCurrency) return;
    setDestinationCurrency(claimDestinationCurrency);
  }, [
    claimDestinationCurrency,
    destinationCurrencyEditable,
    setDestinationCurrency,
    storedDestinationCurrency,
  ]);

  useEffect(() => {
    if (!destinationCurrencyEditable || storedSourceCurrency) return;
    setSourceCurrency(claimSourceCurrency);
  }, [
    claimSourceCurrency,
    destinationCurrencyEditable,
    setSourceCurrency,
    storedSourceCurrency,
  ]);

  const savedCards = useMemo(() => toSavedCards(), []);
  const payment = paymentState.payment;
  const selectedFundingMethod = fundingFromPayment(payment?.type);
  const selectedFundingDetails = selectedFundingMethod
    ? fundRequestFundingDetails[selectedFundingMethod]
    : undefined;

  const numericAmount = amount ? parseFloat(amount.replace(/,/g, "")) : NaN;
  const hasAmount = !Number.isNaN(numericAmount) && numericAmount > 0;
  const quoteSendAmount = hasAmount
    ? amountAnchor === "receive"
      ? Number((numericAmount / MOCK_EXCHANGE_RATE).toFixed(2))
      : numericAmount
    : null;

  const fundingMethod = mapPaymentTypeToMethod(payment?.type) || "card";

  const cardId =
    payment?.type === paymentType.CARD ? payment?.cardId : undefined;

  const { quote, loading: quoteLoading } = useQuote(
    sourceCountry,
    destinationCountry,
    fundingMethod,
    "bank_account",
    quoteSendAmount,
    sourceCurrency,
    cardId
  );

  const sendForBound =
    amountAnchor === "receive"
      ? (quote?.sendAmount ?? quoteSendAmount ?? null)
      : quoteSendAmount;
  const amountBoundVerdict = compareSendToBound(
    sendForBound,
    selectedFundingDetails
  );
  const amountOutOfRange =
    amountBoundVerdict === "below_min" || amountBoundVerdict === "above_max";
  const showFirstSendWarning = isFirstSendWarningVisible({
    sendAmount: sendForBound,
    sourceCurrency,
    fundingCategory: categoryFromFundingMethod(selectedFundingMethod),
  });

  const userData = session.data?.userData;

  const recipientFirstName = useMemo(() => {
    return (
      userData?.name?.firstName?.trim() ||
      userData?.fullName?.trim().split(/\s+/).filter(Boolean)[0] ||
      t("fundRequest.someone")
    );
  }, [userData, t]);

  useEffect(() => {
    if (!payoutEditable) return;
    if (composeSelection) return;
    const seeded = claimDefaultPayoutSelection(userData);
    if (seeded) setComposeSelection(seeded);
  }, [
    payoutEditable,
    composeSelection,
    userData,
    setComposeSelection,
  ]);

  const payoutDestination = useMemo(() => {
    void payoutRevision;
    if (payoutEditable) {
      return payoutRowFromSelection(composeSelection, t, userData);
    }
    return payoutRowFromClaim(userData, t);
  }, [
    payoutEditable,
    composeSelection,
    userData,
    t,
    payoutRevision,
  ]);

  const handleFundingPick = useCallback(
    (pick: FlatFundingPick) => {
      if (pick.kind === "add") {
        setPopup(null);
        navigate(FUND_REQUEST_ROUTES.ADD_CARD);
        return;
      }
      if (pick.kind === "card") {
        setPaymentState({
          payment: {
            cardId: pick.card.paymentMethodId,
            type: paymentType.CARD,
            brand: `${(pick.card.brand || "").toUpperCase()}-${pick.card.last4}`,
          },
        });
        setPopup(null);
        return;
      }
      if (pick.kind === "account") {
        setPaymentState({
          payment: {
            cardId: pick.account.paymentMethodId,
            type: paymentType.BANK,
            brand:
              formatBankAccountLabel(
                pick.account.bankName,
                pick.account.maskedAccountNumber,
              ) ?? pick.account.bankName,
          },
        });
        setPopup(null);
        return;
      }
      if (pick.kind === "cash") {
        setPaymentState({
          payment: {
            cardId: null,
            type: paymentType.CASH,
            brand: t("payment.cashDeposit"),
          },
        });
        setPopup(null);
        return;
      }
      setPaymentState({
        payment: {
          cardId: null,
          type: paymentType.BANK,
          brand: t("transfer.bankAccount", { last4: "" }).trim(),
        },
      });
      setPopup(null);
    },
    [navigate, setPaymentState, t]
  );

  const handleConfirmClick = useCallback(() => {
    if (!payment) {
      setShakePayment(true);
      setTimeout(() => setShakePayment(false), 500);
      return;
    }
    if (!amount || !quote) return;
    if (amountOutOfRange) {
      setPopup("amountLimit");
      return;
    }
    setConfirmLoading(true);
    setQuoteState(quote);
    // Short delay so the gallery can show rainbow-while-submitting.
    setTimeout(() => {
      navigate(FUND_REQUEST_ROUTES.SENDING);
    }, 400);
  }, [
    payment,
    amount,
    quote,
    amountOutOfRange,
    setQuoteState,
    navigate,
  ]);

  const exchangeRate = quote?.exchangeRate ?? 0;
  const fee = quote?.fee ?? 0;

  const mockEconomics = useMemo(
    () =>
      quoteSendAmount != null && quoteSendAmount > 0
        ? buildMockQuote(quoteSendAmount)
        : null,
    [quoteSendAmount]
  );

  const sendFormatted = useMemo(() => {
    const value = quote?.sendAmount ?? mockEconomics?.sendAmount;
    if (value == null) return undefined;
    return amountFormatter(value, sourceCurrency, { country: "en-US" });
  }, [quote?.sendAmount, mockEconomics?.sendAmount, sourceCurrency]);

  const receiveFormatted = useMemo(() => {
    const value = quote?.receiveAmount ?? mockEconomics?.receiveAmount;
    if (value == null) return undefined;
    return amountFormatter(value, destinationCurrency, { country: "en-US" });
  }, [quote?.receiveAmount, mockEconomics?.receiveAmount, destinationCurrency]);

  const totalPayment = useMemo(() => {
    if (quote?.totalCost) return formatLabeledAmount(quote.totalCost, sourceCurrency);
    if (amount) return formatLabeledAmount(parseFloat(amount) + fee, sourceCurrency);
    return formatLabeledAmount(0, sourceCurrency);
  }, [quote, amount, fee, sourceCurrency]);

  const feeDisplay = useMemo(
    () => formatLabeledAmount(fee, sourceCurrency),
    [fee, sourceCurrency]
  );

  const rateText = useMemo(() => {
    if (quoteLoading) return "...";
    return `1 ${sourceCurrency} = ${exchangeRate.toFixed(4)} ${destinationCurrency}`;
  }, [quoteLoading, sourceCurrency, exchangeRate, destinationCurrency]);

  const pickIcon = useCallback((type: paymentType, brand: string) => {
    switch (type) {
      case paymentType.CARD: {
        let finalIcon = null;
        if (brand.toLocaleUpperCase().includes("VISA")) finalIcon = visaLogo;
        if (brand.toLocaleUpperCase().includes("MASTERCARD"))
          finalIcon = masterCardLogo;
        return {
          brand,
          icon: <img src={finalIcon ?? ""} alt={`${brand} logo`} />,
        };
      }
      case paymentType.BANK:
        return { brand, icon: <BankIcon size={20} /> };
      case paymentType.CASH:
        return { brand, icon: <CashSpecialIcon size={20} /> };
      default:
        return { brand, icon: <WalletIcon size={20} /> };
    }
  }, []);

  const paymentSelected = useMemo(
    () =>
      payment
        ? pickIcon(payment.type, payment.brand)
        : {
            brand: t("transfer.selectPaymentMethod"),
            icon: <WalletIcon size={20} />,
          },
    [payment, pickIcon, t]
  );

  const canConfirm = Boolean(amount && payment && quote && !quoteLoading);
  const blockForPricing = Boolean(
    amount && payment && (quoteLoading || !quote)
  );
  const showRainbow = (canConfirm || confirmLoading) && !amountOutOfRange;

  return (
    <>
      <AuraLayout colorConfig={colorConfig} className="scrollable need-help-header">
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            onBackClick={() => navigate(APP_ROUTES.FUND_REQUEST_PAGE)}
            onIconClick={openSupport}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content className="transfer-section">
          <ScrollFadeIndicator color={bodyCopy} />
            <Title
              title={t("transfer.requestedBy", { name: recipientFirstName })}
              align="left"
              color={TITLE_COLOR}
            />
            <section className="estimated-quote-section stacked-quote">
              <section className="quote-container">
                <div className="banrural-amounts">
                  <div className="banrural-send-wrap">
                    <QuoteInfo
                      label={t("transfer.send")}
                      currency={sourceCurrency}
                      textColor={bodyCopy}
                      mode="input"
                      side="send"
                      amountPrefix={sourceAmountPrefix}
                      isActive={amountAnchor === "send"}
                      derivedAmount={
                        amountAnchor === "receive" ? sendFormatted : undefined
                      }
                      readOnly={!amountEditable}
                      showSelectorChevron={destinationCurrencyEditable}
                      onSelect={
                        destinationCurrencyEditable
                          ? () => setPopup("sourceCurrency")
                          : undefined
                      }
                      icon={<FlagCircle iso2={sourceCountry.toLowerCase()} />}
                    />
                    <span
                      className="banrural-swap"
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowExcratePopup(true)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setShowExcratePopup(true);
                        }
                      }}
                      style={{ background: backgroundBtn }}
                    >
                      <ExchangeIcon size={18} colors={[textColorBtn, textColorBtn]} />
                    </span>
                  </div>
                  <QuoteInfo
                    label={t("transfer.receive")}
                    currency={destinationCurrency}
                    textColor={bodyCopy}
                    mode="input"
                    side="receive"
                    amountPrefix={destinationAmountPrefix}
                    isActive={amountAnchor === "receive"}
                    derivedAmount={
                      amountAnchor === "send" ? receiveFormatted : undefined
                    }
                    readOnly={!amountEditable}
                    showSelectorChevron={destinationCurrencyEditable}
                    onSelect={
                      destinationCurrencyEditable
                        ? () => setPopup("destinationCurrency")
                        : undefined
                    }
                    icon={<FlagCircle iso2={destinationCountry.toLowerCase()} />}
                    bottomSlot={
                      <section
                        className="banrural-exchange-rate banrural-exchange-rate--alert"
                        role="button"
                        tabIndex={0}
                        onClick={() => setShowExcratePopup(true)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setShowExcratePopup(true);
                          }
                        }}
                        style={
                          {
                            "--bgc": bannerHighlightBackground ?? bodyCopy,
                            "--colorc": solidStop(
                              bannerHighlightIconColors?.[0] ?? title,
                            ),
                            "--fillc": bannerHighlightColor ?? title,
                          } as CSSProperties
                        }
                      >
                        <span className="banrural-exchange-rate-icon">i</span>
                        <span className="banrural-exchange-rate-text">
                          <b>{rateText}</b>
                          <label>{t("exchange.estimatedRate")}</label>
                        </span>
                      </section>
                    }
                  />
                </div>
              </section>
            </section>
            <PayoutInfo
              title={t("transfer.payingWith")}
              subtitle={paymentSelected.brand}
              icon={paymentSelected.icon}
              background={backgroundBtn}
              color={textColorBtn}
              onChange={() => setPopup("funding")}
              className={`${!payment ? "payment-required" : ""} ${
                shakePayment ? "shake" : ""
              }`.trim()}
            />
            <PayoutInfo
              title={t("transfer.receiveInForRecipient", {
                name: recipientFirstName,
              })}
              subtitle={payoutDestination.subtitle}
              icon={payoutDestination.icon}
              background={payoutEditable ? backgroundBtn : "#f7f7f7"}
              color={payoutEditable ? textColorBtn : TITLE_COLOR}
              locked={!payoutEditable}
              onChange={
                payoutEditable ? () => setPopup("payout") : undefined
              }
            />
            {showFirstSendWarning && firstSend ? (
              <FirstSendWarning copy={firstSend} />
            ) : null}
            <BodyCopy className="footer-text transfer-timing-note" textColor={bodyCopy}>
              <b>{t("transfer.instantTransfer")}</b>
              {" "}
              <span className="transfer-timing-note-rest">
                {t("transfer.mayTakeFew")}
              </span>
            </BodyCopy>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <FooterDetailSummary
              heading={t("transfer.detail")}
              fee={t("transfer.fee", {
                amount: feeDisplay,
              })}
              total={t("transfer.totalPayment", {
                amount: totalPayment,
              })}
              titleColor={TITLE_COLOR}
              textColor={TITLE_COLOR}
            />
            <div className={`button-wrapper ${showRainbow ? "rainbow-ready" : ""}`}>
              <Button
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                disabled={confirmLoading || blockForPricing}
                onClick={handleConfirmClick}
              >
                {confirmLoading
                  ? t("transfer.prepare.loading")
                  : blockForPricing
                    ? t("transfer.loadingQuote")
                    : t("transfer.confirmTransfer")}
              </Button>
            </div>
          </section>
        </Layout.Footer>
      </AuraLayout>
      <Popup
        visible={popup === "amountLimit" && amountOutOfRange}
        onClose={() => setPopup(null)}
      >
        <AmountOutOfRangeInfo
          ns="fund-request"
          kind={amountBoundVerdict === "below_min" ? "below_min" : "above_max"}
          method={t(`funding.methods.${selectedFundingMethod ?? ""}`, {
            defaultValue: selectedFundingMethod ?? "",
          })}
          bound={
            amountBoundVerdict === "below_min"
              ? (selectedFundingDetails?.minAmount ?? 0)
              : (selectedFundingDetails?.maxAmount ?? 0)
          }
          currency={sourceCurrency}
          cta="changeAmount"
          onClose={() => setPopup(null)}
        />
      </Popup>
      <Popup visible={popup === "funding"} onClose={() => setPopup(null)}>
        <SelectFundingMethodSlot
          selectedMethod={selectedFundingMethod}
          selectedPaymentMethodId={
            payment?.type === paymentType.CARD ? payment.cardId : null
          }
          available={fundRequestAvailableFunding}
          cards={savedCards}
          loading={fundingPickerLoading}
          details={fundRequestFundingDetails}
          currency={sourceCurrency}
          onPick={handleFundingPick}
          onClose={() => setPopup(null)}
        />
      </Popup>
      <Popup
        visible={showExcratePopup}
        onClose={() => setShowExcratePopup(false)}
      >
        <section className="qoute-popup">
          <WarningIcon size={60} />
          <BodyCopy textColor={bodyCopy}>
            <Trans i18nKey="transfer.quote.disclaimer" ns="fund-request" />
          </BodyCopy>
        </section>
      </Popup>
      <FundRequestPayoutPopups
        visible={popup === "payout" && payoutEditable}
        onClose={() => setPopup(null)}
        destinationCountry={destinationCountry}
        userData={userData}
        onSelectionChange={() => setPayoutRevision((n) => n + 1)}
      />
      <Popup
        visible={
          (popup === "sourceCurrency" || popup === "destinationCurrency") &&
          destinationCurrencyEditable
        }
        onClose={() => setPopup(null)}
      >
        <SelectDestinationCurrencySlot
          title={t(
            popup === "sourceCurrency"
              ? "transfer.selectSourceCurrency"
              : "transfer.selectDestinationCurrency",
          )}
          options={
            popup === "sourceCurrency"
              ? sourceCurrencyOptions
              : destinationCurrencyOptions
          }
          selectedCurrency={
            popup === "sourceCurrency" ? sourceCurrency : destinationCurrency
          }
          onSelect={(currencyCode) => {
            setQuoteState(null);
            if (popup === "sourceCurrency") {
              setSourceCurrency(currencyCode);
            } else {
              setDestinationCurrency(currencyCode);
            }
            if (payoutEditable) {
              const defaultSelection = claimDefaultPayoutSelection(userData);
              if (defaultSelection) {
                setComposeSelection(defaultSelection);
                setPayoutRevision((n) => n + 1);
              }
            }
          }}
          onClose={() => setPopup(null)}
        />
      </Popup>
      {supportSheet}
    </>
  );
});
